import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  CreateFamsAssetCategoryPayload,
  CreateFamsManagerNodePayload,
  FamsAssetCategoryStats,
  FamsAssetCategoryTreeResponse,
  FamsHierarchyNodeDetail,
  FamsHierarchyNodeItem,
  FamsManagerNodeItem,
  UpdateFamsAssetCategoryPayload,
  UpdateFamsAssetCategoryOrderPayload,
  UpdateFamsManagerNodePayload,
} from './types/fams-master';

const request = createAppRequest();

export async function getFamsManagerNodesTree(options?: ApiRequestOptions) {
  return request<FamsManagerNodeItem[]>('/bff/fams/manager-nodes/tree', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getFamsHierarchyTree(options?: ApiRequestOptions) {
  return request<FamsHierarchyNodeItem[]>('/bff/fams/hierarchy/tree', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getFamsHierarchyNodeDetail(
  params: { nodeType: string; nodeKey: string },
  options?: ApiRequestOptions,
) {
  return request<FamsHierarchyNodeDetail>('/bff/fams/hierarchy/node-detail', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createFamsManagerNode(
  payload: CreateFamsManagerNodePayload,
  options?: ApiRequestOptions,
) {
  return request<FamsManagerNodeItem>('/bff/fams/manager-nodes', {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateFamsManagerNode(
  id: number,
  payload: UpdateFamsManagerNodePayload,
  options?: ApiRequestOptions,
) {
  return request<FamsManagerNodeItem>(`/bff/fams/manager-nodes/${id}`, {
    method: 'PUT',
    data: payload,
    ...(options || {}),
  });
}

export async function updateFamsManagerNodeState(
  id: number,
  state: 0 | 1,
  options?: ApiRequestOptions,
) {
  return request<FamsManagerNodeItem>(`/bff/fams/manager-nodes/${id}/state`, {
    method: 'PATCH',
    data: { state },
    ...(options || {}),
  });
}

export async function getFamsAssetCategoriesTree(
  params?: { keyword?: string; includeDisabled?: 0 | 1 },
  options?: ApiRequestOptions,
) {
  return request<FamsAssetCategoryTreeResponse>('/bff/fams/asset-categories/tree', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getFamsAssetCategoriesStats(options?: ApiRequestOptions) {
  return request<FamsAssetCategoryStats>('/bff/fams/asset-categories/stats', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createFamsAssetCategory(
  payload: CreateFamsAssetCategoryPayload,
  options?: ApiRequestOptions,
) {
  return request<Record<string, unknown>>('/bff/fams/asset-categories', {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateFamsAssetCategory(
  id: number,
  payload: UpdateFamsAssetCategoryPayload,
  options?: ApiRequestOptions,
) {
  return request<Record<string, unknown>>(`/bff/fams/asset-categories/${id}`, {
    method: 'PUT',
    data: payload,
    ...(options || {}),
  });
}

export async function updateFamsAssetCategoryOrder(
  payload: UpdateFamsAssetCategoryOrderPayload,
  options?: ApiRequestOptions,
) {
  return request<{ updated: number }>('/bff/fams/asset-categories/order', {
    method: 'PUT',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteFamsAssetCategory(
  id: number,
  params?: { hardDelete?: 0 | 1 },
  options?: ApiRequestOptions,
) {
  return request<Record<string, unknown>>(`/bff/fams/asset-categories/${id}`, {
    method: 'DELETE',
    params,
    ...(options || {}),
  });
}
